/**
 * School Store
 *
 * Manages school administration state for the SA (School Admin) and Teacher roles.
 * Covers the full school hierarchy: school settings, classes, student/teacher rosters,
 * grade levels, class assignments, challenge requests, and survey campaigns.
 * Supports CSV bulk import for student enrollment and class-level dashboard stats.
 *
 * @module useSchoolStore
 *
 * @example
 * ```tsx
 * import { useSchoolStore, selectClasses, selectStudents } from '@/store/useSchoolStore'
 *
 * function SchoolDashboard() {
 *   const { fetchMySchool, fetchClasses, fetchStudents } = useSchoolStore()
 *   const classes = useSchoolStore(selectClasses)
 *   const students = useSchoolStore(selectStudents)
 *
 *   useEffect(() => {
 *     fetchMySchool()
 *     fetchClasses()
 *   }, [])
 *
 *   return <ClassRoster classes={classes} students={students} />
 * }
 * ```
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  School,
  SchoolClass,
  StudentRoster,
  TeacherRoster,
  ClassAssignment,
  SchoolDashboardStats,
  ClassDashboardStats,
  CSVImportResult,
  GradeLevel,
  PurchaseRequest,
  ChallengeRequest,
  Survey,
  SurveyCampaign,
  GraduatingStudent,
  TeacherInvitation,
  SchoolAnalyticsData,
  SurveyTemplate,
} from '../types'
import { schoolApi } from '../api'

interface SchoolState {
  // School data
  school: School | null
  stats: SchoolDashboardStats | null

  // Classes
  classes: SchoolClass[]
  currentClass: SchoolClass | null
  classStats: ClassDashboardStats | null

  // Roster
  students: StudentRoster[]
  teachers: TeacherRoster[]

  // Assignments
  assignments: ClassAssignment[]

  // Grade levels
  gradeLevels: GradeLevel[]
  isLoadingGrades: boolean

  // Purchase requests
  purchaseRequests: PurchaseRequest[]
  isLoadingPurchaseRequests: boolean

  // Loading states
  isLoading: boolean
  isLoadingClasses: boolean
  isLoadingStudents: boolean
  isLoadingAssignments: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions - School
  fetchMySchool: () => Promise<void>
  fetchSchoolStats: () => Promise<void>
  updateSchool: (data: Partial<School>) => Promise<boolean>

  // Actions - Classes
  fetchClasses: () => Promise<void>
  createClass: (data: {
    name: string
    description?: string
    gradeLevel?: string
    primaryTeacherId: string
  }) => Promise<SchoolClass | null>
  updateClass: (classId: string, data: Partial<SchoolClass>) => Promise<boolean>
  setCurrentClass: (cls: SchoolClass | null) => void
  fetchClassStats: (classId: string) => Promise<void>

  // Actions - Students
  fetchStudents: (classId?: string) => Promise<void>
  addStudent: (data: {
    userId: string
    classId?: string
    studentId?: string
    gradeLevel?: string
  }) => Promise<boolean>
  removeStudent: (studentRosterId: string) => Promise<boolean>
  suspendStudent: (studentRosterId: string, reason?: string) => Promise<boolean>
  reinstateStudent: (studentRosterId: string) => Promise<boolean>
  importStudentsCSV: (csvData: string) => Promise<CSVImportResult | null>

  // Actions - Teachers
  fetchTeachers: () => Promise<void>
  addTeacher: (data: {
    userId: string
    role: 'teacher' | 'admin' | 'staff'
    department?: string
  }) => Promise<boolean>

  // Actions - Assignments
  fetchAssignments: (classId: string) => Promise<void>
  createAssignment: (
    classId: string,
    data: {
      challengeId: string
      title?: string
      instructions?: string
      dueDate?: string
    }
  ) => Promise<ClassAssignment | null>
  updateAssignment: (
    assignmentId: string,
    data: Partial<ClassAssignment>
  ) => Promise<boolean>

  // Actions - Purchase Requests
  fetchPurchaseRequests: (status?: 'pending' | 'approved' | 'denied') => Promise<void>
  createPurchaseRequest: (data: {
    challengeId: string
    classId?: string
    studentCount: number
    reason?: string
    estimatedCost: number
  }) => Promise<boolean>
  reviewPurchaseRequest: (
    requestId: string,
    decision: 'approved' | 'denied',
    note?: string
  ) => Promise<boolean>

  // Challenge Requests
  challengeRequests: ChallengeRequest[]
  isLoadingRequests: boolean

  // Surveys
  surveys: Survey[]
  campaigns: SurveyCampaign[]
  isLoadingSurveys: boolean

  // Teacher Invitations
  pendingInvitations: TeacherInvitation[]
  isLoadingInvitations: boolean

  // Analytics
  analytics: SchoolAnalyticsData | null
  isLoadingAnalytics: boolean

  // Survey Templates
  surveyTemplates: SurveyTemplate[]
  isLoadingTemplates: boolean

  // Alumni Transition
  graduatingStudents: GraduatingStudent[]
  graduatingLoading: boolean

  // Actions - Teacher Invitations
  inviteTeacher: (data: { email: string; role: 'teacher' | 'admin' | 'staff'; department?: string }) => Promise<boolean>
  fetchPendingInvitations: () => Promise<void>
  cancelInvitation: (invitationId: string) => Promise<boolean>
  generateInviteLink: () => Promise<string | null>
  removeTeacher: (teacherRosterId: string) => Promise<boolean>

  // Actions - Roster Export
  exportStudentsCSV: () => Promise<string | null>

  // Actions - Analytics
  fetchAnalytics: (dateRange?: { start: string; end: string }) => Promise<void>

  // Actions - Survey Templates
  fetchSurveyTemplates: () => Promise<void>
  createSurveyTemplate: (data: { title: string; description?: string; externalUrl: string }) => Promise<SurveyTemplate | null>
  deleteSurveyTemplate: (templateId: string) => Promise<boolean>
  createSurveyFromTemplate: (templateId: string) => Promise<Survey | null>

  // Actions - Survey Scheduling
  scheduleCampaign: (surveyId: string, data: { targetAudience: SurveyCampaign['targetAudience']; scheduledAt: string; targetClassId?: string; targetGradeLevel?: string }) => Promise<boolean>
  createReminder: (campaignId: string, scheduledAt: string) => Promise<boolean>

  // Actions - Challenge Requests
  createChallengeRequest: (data: { challengeId: string; reason?: string; numberOfStudents?: number; classId?: string }) => Promise<boolean>
  fetchChallengeRequests: (status?: 'pending' | 'approved' | 'denied') => Promise<void>
  reviewChallengeRequest: (requestId: string, decision: { status: 'approved' | 'denied'; reviewNote?: string }) => Promise<boolean>

  // Actions - Surveys
  fetchSurveys: () => Promise<void>
  createSurvey: (data: { title: string; description?: string; externalUrl: string }) => Promise<Survey | null>
  updateSurvey: (surveyId: string, data: Partial<Pick<Survey, 'title' | 'description' | 'externalUrl' | 'status'>>) => Promise<boolean>
  deleteSurvey: (surveyId: string) => Promise<boolean>
  createCampaign: (surveyId: string, data: { targetAudience: SurveyCampaign['targetAudience']; targetClassId?: string; targetGradeLevel?: string }) => Promise<boolean>
  fetchCampaigns: (surveyId: string) => Promise<void>

  // Actions - Grade Levels
  fetchGradeLevels: () => Promise<void>
  createGradeLevel: (name: string) => Promise<GradeLevel | null>
  updateGradeLevel: (gradeId: string, data: { name?: string; isActive?: boolean }) => Promise<boolean>
  deleteGradeLevel: (gradeId: string) => Promise<boolean>

  // Actions - Alumni Transition
  fetchGraduatingStudents: (schoolId: string) => Promise<void>
  transitionStudent: (studentId: string) => Promise<boolean>
  archiveStudent: (studentId: string) => Promise<boolean>
  exportAndDeleteStudent: (studentId: string) => Promise<boolean>
  batchTransitionStudents: (studentIds: string[]) => Promise<boolean>

  clearError: () => void
  reset: () => void
}

const initialState = {
  school: null,
  stats: null,
  classes: [],
  currentClass: null,
  classStats: null,
  students: [],
  teachers: [],
  assignments: [],
  gradeLevels: [] as GradeLevel[],
  isLoadingGrades: false,
  pendingInvitations: [] as TeacherInvitation[],
  isLoadingInvitations: false,
  analytics: null as SchoolAnalyticsData | null,
  isLoadingAnalytics: false,
  surveyTemplates: [] as SurveyTemplate[],
  isLoadingTemplates: false,
  purchaseRequests: [] as PurchaseRequest[],
  isLoadingPurchaseRequests: false,
  challengeRequests: [] as ChallengeRequest[],
  isLoadingRequests: false,
  surveys: [] as Survey[],
  campaigns: [] as SurveyCampaign[],
  isLoadingSurveys: false,
  graduatingStudents: [] as GraduatingStudent[],
  graduatingLoading: false,
  isLoading: false,
  isLoadingClasses: false,
  isLoadingStudents: false,
  isLoadingAssignments: false,
  isSaving: false,
  error: null,
}

export const useSchoolStore = createWithEqualityFn<SchoolState>()((set, get) => ({
  ...initialState,

  // ============================================================================
  // School
  // ============================================================================

  fetchMySchool: async () => {
    set({ isLoading: true, error: null })

    const response = await schoolApi.getMySchool()

    if (response.success) {
      set({ school: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load school',
        isLoading: false,
      })
    }
  },

  fetchSchoolStats: async () => {
    const { school } = get()
    if (!school) return

    const response = await schoolApi.getSchoolStats(school.id)

    if (response.success && response.data) {
      set({ stats: response.data })
    }
  },

  updateSchool: async (data) => {
    const { school } = get()
    if (!school) return false

    set({ isSaving: true, error: null })

    const response = await schoolApi.updateSchool(school.id, data)

    if (response.success && response.data) {
      set({ school: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update school',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Classes
  // ============================================================================

  fetchClasses: async () => {
    const { school } = get()
    if (!school) return

    set({ isLoadingClasses: true, error: null })

    const response = await schoolApi.getClasses(school.id)

    if (response.success && response.data) {
      set({ classes: response.data, isLoadingClasses: false })
    } else {
      set({
        error: response.error ?? 'Failed to load classes',
        isLoadingClasses: false,
      })
    }
  },

  createClass: async (data) => {
    const { school } = get()
    if (!school) return null

    set({ isSaving: true, error: null })

    const response = await schoolApi.createClass(school.id, data)

    if (response.success && response.data) {
      set({
        classes: [...get().classes, response.data],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create class',
        isSaving: false,
      })
      return null
    }
  },

  updateClass: async (classId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.updateClass(classId, data)

    if (response.success && response.data) {
      set({
        classes: get().classes.map((c) =>
          c.id === classId ? response.data! : c
        ),
        currentClass:
          get().currentClass?.id === classId ? response.data : get().currentClass,
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update class',
        isSaving: false,
      })
      return false
    }
  },

  setCurrentClass: (cls) => {
    set({ currentClass: cls })
  },

  fetchClassStats: async (classId) => {
    const response = await schoolApi.getClassStats(classId)

    if (response.success && response.data) {
      set({ classStats: response.data })
    }
  },

  // ============================================================================
  // Students
  // ============================================================================

  fetchStudents: async (classId) => {
    const { school } = get()
    if (!school) return

    set({ isLoadingStudents: true, error: null })

    const response = await schoolApi.getStudents(school.id, classId)

    if (response.success && response.data) {
      set({ students: response.data, isLoadingStudents: false })
    } else {
      set({
        error: response.error ?? 'Failed to load students',
        isLoadingStudents: false,
      })
    }
  },

  addStudent: async (data) => {
    const { school } = get()
    if (!school) return false

    set({ isSaving: true, error: null })

    const response = await schoolApi.addStudent(school.id, data)

    if (response.success && response.data) {
      set({
        students: [...get().students, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to add student',
        isSaving: false,
      })
      return false
    }
  },

  removeStudent: async (studentRosterId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.removeStudent(studentRosterId)

    if (response.success) {
      set({
        students: get().students.map((s) =>
          s.id === studentRosterId ? { ...s, status: 'inactive' as const } : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to remove student',
        isSaving: false,
      })
      return false
    }
  },

  suspendStudent: async (studentRosterId, reason) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.suspendStudent(studentRosterId, reason)

    if (response.success && response.data) {
      set({
        students: get().students.map((s) =>
          s.id === studentRosterId ? response.data! : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to suspend student',
        isSaving: false,
      })
      return false
    }
  },

  reinstateStudent: async (studentRosterId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.reinstateStudent(studentRosterId)

    if (response.success && response.data) {
      set({
        students: get().students.map((s) =>
          s.id === studentRosterId ? response.data! : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to reinstate student',
        isSaving: false,
      })
      return false
    }
  },

  importStudentsCSV: async (csvData) => {
    const { school } = get()
    if (!school) return null

    set({ isSaving: true, error: null })

    const response = await schoolApi.importStudentsCSV(school.id, csvData)

    if (response.success && response.data) {
      set({ isSaving: false })
      // Refresh students after import
      await get().fetchStudents()
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to import students',
        isSaving: false,
      })
      return null
    }
  },

  // ============================================================================
  // Teachers
  // ============================================================================

  fetchTeachers: async () => {
    const { school } = get()
    if (!school) return

    const response = await schoolApi.getTeachers(school.id)

    if (response.success && response.data) {
      set({ teachers: response.data })
    }
  },

  addTeacher: async (data) => {
    const { school } = get()
    if (!school) return false

    set({ isSaving: true, error: null })

    const response = await schoolApi.addTeacher(school.id, data)

    if (response.success && response.data) {
      set({
        teachers: [...get().teachers, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to add teacher',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Assignments
  // ============================================================================

  fetchAssignments: async (classId) => {
    set({ isLoadingAssignments: true, error: null })

    const response = await schoolApi.getClassAssignments(classId)

    if (response.success && response.data) {
      set({ assignments: response.data, isLoadingAssignments: false })
    } else {
      set({
        error: response.error ?? 'Failed to load assignments',
        isLoadingAssignments: false,
      })
    }
  },

  createAssignment: async (classId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.createAssignment(classId, data)

    if (response.success && response.data) {
      set({
        assignments: [response.data, ...get().assignments],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create assignment',
        isSaving: false,
      })
      return null
    }
  },

  updateAssignment: async (assignmentId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.updateAssignment(assignmentId, data)

    if (response.success && response.data) {
      set({
        assignments: get().assignments.map((a) =>
          a.id === assignmentId ? response.data! : a
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update assignment',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Purchase Requests
  // ============================================================================

  fetchPurchaseRequests: async (status) => {
    const { school } = get()
    if (!school) return

    set({ isLoadingPurchaseRequests: true, error: null })

    const response = await schoolApi.getPurchaseRequests(school.id, status)

    if (response.success && response.data) {
      set({ purchaseRequests: response.data, isLoadingPurchaseRequests: false })
    } else {
      set({
        error: response.error ?? 'Failed to load purchase requests',
        isLoadingPurchaseRequests: false,
      })
    }
  },

  createPurchaseRequest: async (data) => {
    const { school } = get()
    if (!school) return false

    set({ isSaving: true, error: null })

    const response = await schoolApi.createPurchaseRequest(school.id, data)

    if (response.success && response.data) {
      set({
        purchaseRequests: [response.data, ...get().purchaseRequests],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to create purchase request',
        isSaving: false,
      })
      return false
    }
  },

  reviewPurchaseRequest: async (requestId, decision, note) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.reviewPurchaseRequest(requestId, decision, note)

    if (response.success && response.data) {
      set({
        purchaseRequests: get().purchaseRequests.map((r) =>
          r.id === requestId ? response.data! : r
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to review purchase request',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Grade Levels
  // ============================================================================

  fetchGradeLevels: async () => {
    const { school } = get()
    if (!school) return

    set({ isLoadingGrades: true, error: null })

    const response = await schoolApi.getGradeLevels(school.id)

    if (response.success && response.data) {
      set({ gradeLevels: response.data, isLoadingGrades: false })
    } else {
      set({
        error: response.error ?? 'Failed to load grade levels',
        isLoadingGrades: false,
      })
    }
  },

  createGradeLevel: async (name) => {
    const { school, gradeLevels } = get()
    if (!school) return null

    set({ isSaving: true, error: null })

    const response = await schoolApi.createGradeLevel(school.id, {
      name,
      displayOrder: gradeLevels.length + 1,
    })

    if (response.success && response.data) {
      set({
        gradeLevels: [...get().gradeLevels, response.data],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create grade level',
        isSaving: false,
      })
      return null
    }
  },

  updateGradeLevel: async (gradeId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.updateGradeLevel(gradeId, data)

    if (response.success && response.data) {
      set({
        gradeLevels: get().gradeLevels.map((g) =>
          g.id === gradeId ? response.data! : g
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update grade level',
        isSaving: false,
      })
      return false
    }
  },

  deleteGradeLevel: async (gradeId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.deleteGradeLevel(gradeId)

    if (response.success) {
      set({
        gradeLevels: get().gradeLevels.filter((g) => g.id !== gradeId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete grade level',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Challenge Requests
  // ============================================================================

  createChallengeRequest: async (data) => {
    const { school } = get()
    if (!school) return false

    set({ isSaving: true, error: null })

    const response = await schoolApi.createChallengeRequest(school.id, data)

    if (response.success) {
      set({ isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to create challenge request',
        isSaving: false,
      })
      return false
    }
  },

  fetchChallengeRequests: async (status) => {
    const { school } = get()
    if (!school) return

    set({ isLoadingRequests: true, error: null })

    const response = await schoolApi.getChallengeRequests(school.id, status)

    if (response.success && response.data) {
      set({ challengeRequests: response.data, isLoadingRequests: false })
    } else {
      set({
        error: response.error ?? 'Failed to load challenge requests',
        isLoadingRequests: false,
      })
    }
  },

  reviewChallengeRequest: async (requestId, decision) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.reviewChallengeRequest(requestId, decision)

    if (response.success && response.data) {
      set({
        challengeRequests: get().challengeRequests.map((r) =>
          r.id === requestId ? response.data! : r
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to review challenge request',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Surveys
  // ============================================================================

  fetchSurveys: async () => {
    const { school } = get()
    if (!school) return

    set({ isLoadingSurveys: true, error: null })

    const response = await schoolApi.getSurveys(school.id)

    if (response.success && response.data) {
      set({ surveys: response.data, isLoadingSurveys: false })
    } else {
      set({
        error: response.error ?? 'Failed to load surveys',
        isLoadingSurveys: false,
      })
    }
  },

  createSurvey: async (data) => {
    const { school } = get()
    if (!school) return null

    set({ isSaving: true, error: null })

    const response = await schoolApi.createSurvey(school.id, data)

    if (response.success && response.data) {
      set({
        surveys: [response.data, ...get().surveys],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create survey',
        isSaving: false,
      })
      return null
    }
  },

  updateSurvey: async (surveyId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.updateSurvey(surveyId, data)

    if (response.success && response.data) {
      set({
        surveys: get().surveys.map((s) =>
          s.id === surveyId ? response.data! : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update survey',
        isSaving: false,
      })
      return false
    }
  },

  deleteSurvey: async (surveyId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.deleteSurvey(surveyId)

    if (response.success) {
      set({
        surveys: get().surveys.filter((s) => s.id !== surveyId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to delete survey',
        isSaving: false,
      })
      return false
    }
  },

  createCampaign: async (surveyId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.createCampaign(surveyId, data)

    if (response.success && response.data) {
      set({
        campaigns: [...get().campaigns, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to create campaign',
        isSaving: false,
      })
      return false
    }
  },

  fetchCampaigns: async (surveyId) => {
    const response = await schoolApi.getCampaigns(surveyId)

    if (response.success && response.data) {
      set({ campaigns: response.data })
    }
  },

  // ============================================================================
  // Alumni Transition
  // ============================================================================

  fetchGraduatingStudents: async (schoolId) => {
    set({ graduatingLoading: true, error: null })

    const response = await schoolApi.getGraduatingStudents(schoolId)

    if (response.success && response.data) {
      set({ graduatingStudents: response.data, graduatingLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load graduating students',
        graduatingLoading: false,
      })
    }
  },

  transitionStudent: async (studentId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.transitionStudent(studentId)

    if (response.success) {
      set({
        graduatingStudents: get().graduatingStudents.map((s) =>
          s.id === studentId ? { ...s, status: 'transitioned' as const } : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to transition student',
        isSaving: false,
      })
      return false
    }
  },

  archiveStudent: async (studentId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.archiveStudent(studentId)

    if (response.success) {
      set({
        graduatingStudents: get().graduatingStudents.map((s) =>
          s.id === studentId ? { ...s, status: 'opted-out' as const } : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to archive student',
        isSaving: false,
      })
      return false
    }
  },

  exportAndDeleteStudent: async (studentId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.exportAndDeleteStudent(studentId)

    if (response.success) {
      set({
        graduatingStudents: get().graduatingStudents.filter((s) => s.id !== studentId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to export and delete student',
        isSaving: false,
      })
      return false
    }
  },

  batchTransitionStudents: async (studentIds) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.batchTransitionStudents(studentIds)

    if (response.success) {
      set({
        graduatingStudents: get().graduatingStudents.map((s) =>
          studentIds.includes(s.id) ? { ...s, status: 'transitioned' as const } : s
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to batch transition students',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Teacher Invitations
  // ============================================================================

  inviteTeacher: async (data) => {
    const school = get().school
    if (!school) return false
    set({ isSaving: true, error: null })

    const response = await schoolApi.inviteTeacher(school.id, data)
    if (response.success && response.data) {
      set({
        pendingInvitations: [...get().pendingInvitations, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to send invitation', isSaving: false })
      return false
    }
  },

  fetchPendingInvitations: async () => {
    const school = get().school
    if (!school) return
    set({ isLoadingInvitations: true })

    const response = await schoolApi.getPendingInvitations(school.id)
    if (response.success) {
      set({ pendingInvitations: response.data ?? [], isLoadingInvitations: false })
    } else {
      set({ error: response.error ?? 'Failed to load invitations', isLoadingInvitations: false })
    }
  },

  cancelInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.cancelInvitation(invitationId)
    if (response.success) {
      set({
        pendingInvitations: get().pendingInvitations.filter((i) => i.id !== invitationId),
        isSaving: false,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to cancel invitation', isSaving: false })
      return false
    }
  },

  generateInviteLink: async () => {
    const school = get().school
    if (!school) return null

    const response = await schoolApi.generateInviteLink(school.id)
    if (response.success && response.data) {
      return response.data.url
    }
    return null
  },

  removeTeacher: async (teacherRosterId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.removeTeacher(teacherRosterId)
    if (response.success) {
      set({
        teachers: get().teachers.map((t) =>
          t.id === teacherRosterId ? { ...t, status: 'inactive' as const } : t
        ),
        isSaving: false,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to remove teacher', isSaving: false })
      return false
    }
  },

  // ============================================================================
  // Roster Export
  // ============================================================================

  exportStudentsCSV: async () => {
    const school = get().school
    if (!school) return null
    set({ isSaving: true, error: null })

    const response = await schoolApi.exportStudentsCSV(school.id)
    if (response.success && response.data) {
      set({ isSaving: false })
      return response.data
    } else {
      set({ error: response.error ?? 'Failed to export roster', isSaving: false })
      return null
    }
  },

  // ============================================================================
  // Analytics
  // ============================================================================

  fetchAnalytics: async (dateRange?) => {
    const school = get().school
    if (!school) return
    set({ isLoadingAnalytics: true })

    const response = await schoolApi.getAnalytics(school.id, dateRange)
    if (response.success) {
      set({ analytics: response.data ?? null, isLoadingAnalytics: false })
    } else {
      set({ error: response.error ?? 'Failed to load analytics', isLoadingAnalytics: false })
    }
  },

  // ============================================================================
  // Survey Templates
  // ============================================================================

  fetchSurveyTemplates: async () => {
    const school = get().school
    if (!school) return
    set({ isLoadingTemplates: true })

    const response = await schoolApi.getSurveyTemplates(school.id)
    if (response.success) {
      set({ surveyTemplates: response.data ?? [], isLoadingTemplates: false })
    } else {
      set({ error: response.error ?? 'Failed to load templates', isLoadingTemplates: false })
    }
  },

  createSurveyTemplate: async (data) => {
    const school = get().school
    if (!school) return null
    set({ isSaving: true, error: null })

    const response = await schoolApi.createSurveyTemplate(school.id, data)
    if (response.success && response.data) {
      set({
        surveyTemplates: [...get().surveyTemplates, response.data],
        isSaving: false,
      })
      return response.data
    } else {
      set({ error: response.error ?? 'Failed to create template', isSaving: false })
      return null
    }
  },

  deleteSurveyTemplate: async (templateId) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.deleteSurveyTemplate(templateId)
    if (response.success) {
      set({
        surveyTemplates: get().surveyTemplates.filter((t) => t.id !== templateId),
        isSaving: false,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to delete template', isSaving: false })
      return false
    }
  },

  createSurveyFromTemplate: async (templateId) => {
    const school = get().school
    if (!school) return null
    set({ isSaving: true, error: null })

    const response = await schoolApi.createSurveyFromTemplate(school.id, templateId)
    if (response.success && response.data) {
      set({
        surveys: [response.data, ...get().surveys],
        isSaving: false,
      })
      return response.data
    } else {
      set({ error: response.error ?? 'Failed to create survey from template', isSaving: false })
      return null
    }
  },

  // ============================================================================
  // Survey Scheduling
  // ============================================================================

  scheduleCampaign: async (surveyId, data) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.scheduleCampaign(surveyId, data)
    if (response.success && response.data) {
      set({
        campaigns: [...get().campaigns, response.data],
        isSaving: false,
      })
      return true
    } else {
      set({ error: response.error ?? 'Failed to schedule campaign', isSaving: false })
      return false
    }
  },

  createReminder: async (campaignId, scheduledAt) => {
    set({ isSaving: true, error: null })

    const response = await schoolApi.createReminder(campaignId, scheduledAt)
    if (response.success) {
      set({ isSaving: false })
      return true
    } else {
      set({ error: response.error ?? 'Failed to create reminder', isSaving: false })
      return false
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// ============================================================================
// Selectors
// ============================================================================

/** Select the current user's school entity. */
export const selectSchool = (state: SchoolState) => state.school

/** Select school-wide dashboard statistics (enrollment, activity, completion rates). */
export const selectSchoolStats = (state: SchoolState) => state.stats

/** Select all classes for the school. */
export const selectClasses = (state: SchoolState) => state.classes

/** Select the student roster (optionally filtered by class). */
export const selectStudents = (state: SchoolState) => state.students

/** Select the teacher roster for the school. */
export const selectTeachers = (state: SchoolState) => state.teachers

/** Select challenge assignments for the currently viewed class. */
export const selectAssignments = (state: SchoolState) => state.assignments

/** Select graduating students for the alumni transition workflow. */
export const selectGraduatingStudents = (state: SchoolState) => state.graduatingStudents

/** Select pending teacher invitations. */
export const selectPendingInvitations = (state: SchoolState) => state.pendingInvitations

/** Select school analytics data. */
export const selectAnalytics = (state: SchoolState) => state.analytics

/** Select saved survey templates. */
export const selectSurveyTemplates = (state: SchoolState) => state.surveyTemplates

export const selectPurchaseRequests = (state: SchoolState) => state.purchaseRequests
